import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { CurrencyCode } from '@watershed/constants/currency';
import { GQCountryAlpha2 } from '@watershed/shared-universal/generated/graphql-schema-types';
import country_constants from '@watershed/shared-universal/generated/shared_data/geo/country_constants.json';

import {
  getCountryName,
  WS_ISO3166_COUNTRIES,
  WS_ISO3166_COUNTRY_BY_ALPHA_2,
} from './geo/countries';

export type Country = {
  name: string;
  currencyCode: CurrencyCode;
  alternativeNames?: ReadonlyArray<string>;
  'alpha-2': GQCountryAlpha2;
  'country-code': string;
  'alpha-3': string;
};

// Pulled from https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes/blob/master/slim-2/slim-2.json merged with https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes/blob/master/slim-3/slim-3.json
export const COUNTRIES_CONST = country_constants as ReadonlyArray<Country>;
export type CountryName = (typeof COUNTRIES_CONST)[number]['name'];
export const countries: ReadonlyArray<Country> = COUNTRIES_CONST;

export const COUNTRY_SELECT_LIST_OPTIONS = countries.map((country) => ({
  value: country['alpha-2'],
  display: country.name,
}));

export const COUNTRY_AND_EU_SELECT_LIST_OPTIONS = orderBy(
  (
    COUNTRY_SELECT_LIST_OPTIONS as Array<{ value: string; display: string }>
  ).concat([
    {
      value: 'EU',
      display: 'European Union',
    },
  ]),
  (o) => o.display,
  ['asc']
);

export function getCurrencyCodeForCountryCode(
  countryCodeAlpha2: string
): CurrencyCode | undefined {
  return countries.find((country) => country['alpha-2'] === countryCodeAlpha2)
    ?.currencyCode;
}

export const countryByCode = keyBy(countries, (country) => country['alpha-2']);
export const countryByName = keyBy(countries, (country) => country['name']);

function countryToAltNames(c: Country) {
  return [c.name, c['alpha-2'], c['alpha-3'], ...(c.alternativeNames ?? [])];
}

const countryAltsToCanonical = Object.fromEntries(
  countries.flatMap((c) => countryToAltNames(c).map((alt) => [alt, c.name]))
);
export function cleanCountryName(country?: string | null): string {
  return countryAltsToCanonical[country ?? ''];
}

export function getIsoCountryName(countryAlpha2?: string | null): string {
  // Puerto Rico is the only country that is being overridden to be US
  if (countryAlpha2 === 'PR') {
    return getCountryName(WS_ISO3166_COUNTRY_BY_ALPHA_2['US']) ?? '';
  }
  const country = WS_ISO3166_COUNTRY_BY_ALPHA_2[countryAlpha2 ?? ''];

  return country ? getCountryName(country) : '';
}

export const validCountryAlts = COUNTRIES_CONST.flatMap(countryToAltNames);

export const validCurrencies = uniq(COUNTRIES_CONST.map((c) => c.currencyCode));

export const wsIso3166CountryAltsToCanonical = Object.fromEntries(
  WS_ISO3166_COUNTRIES.flatMap((c) =>
    [
      c.name,
      ...(c.aliases ?? []),
      ...(c.official_name ? [c.official_name] : []),
      ...(c.common_name ? [c.common_name] : []),
      c.alpha_2,
      c.alpha_3,
    ].map((alt) => [alt, getCountryName(c)])
  )
);
export function cleanIso3166CountryName(
  country?: string | null
): string | null {
  if (!country) {
    return null;
  }
  return wsIso3166CountryAltsToCanonical[country];
}
